import React, { useState } from 'react';
import { motion } from 'framer-motion';
import BookingCalendar from '../components/BookingCalendar';
import BookingForm, { BookingData } from '../components/BookingForm';
import OTPVerification from '../components/OTPVerification';
import { OTPService, BookingService } from '../services/firebase';

type BookingStep = 'calendar' | 'form' | 'otp' | 'confirmation';

const BookingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<BookingStep>('calendar');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(false);

  const handleTimeSelect = (date: string, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
    if (date && time) {
      setCurrentStep('form');
    }
  };

  const handleBookingSubmit = async (data: BookingData) => {
    setBookingData(data);
    setCurrentStep('otp');
    
    // Send OTP to email
    const otpService = OTPService.getInstance();
    const result = await otpService.sendOTP(data.email);
    
    if (!result.success) {
      console.error('Failed to send OTP:', result.message);
      // You might want to show an error message to the user
    }
  };

  const handleOTPVerify = async (otp: string) => {
    setLoading(true);
    try {
      const otpService = OTPService.getInstance();
      const result = await otpService.verifyOTP(bookingData?.email || '', otp);
      
      if (result.success) {
        // Create booking
        const bookingService = BookingService.getInstance();
        const bookingResult = await bookingService.createBooking(bookingData);
        
        if (bookingResult.success) {
          // Send confirmation email
          await bookingService.sendConfirmationEmail(bookingData);
          setCurrentStep('confirmation');
        } else {
          console.error('Failed to create booking:', bookingResult.message);
        }
      } else {
        console.error('OTP verification failed:', result.message);
        // You might want to show an error message to the user
      }
    } catch (error) {
      console.error('OTP verification failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPResend = async () => {
    setLoading(true);
    try {
      const otpService = OTPService.getInstance();
      const result = await otpService.sendOTP(bookingData?.email || '');
      
      if (!result.success) {
        console.error('Failed to resend OTP:', result.message);
        // You might want to show an error message to the user
      }
    } catch (error) {
      console.error('Failed to resend OTP:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCalendar = () => {
    setCurrentStep('calendar');
    setSelectedDate('');
    setSelectedTime('');
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'calendar':
        return (
          <BookingCalendar
            onTimeSelect={handleTimeSelect}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
          />
        );

      case 'form':
        return (
          <BookingForm
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onBookingSubmit={handleBookingSubmit}
            onBack={handleBackToCalendar}
          />
        );

      case 'otp':
        return (
          <OTPVerification
            email={bookingData?.email || ''}
            onVerify={handleOTPVerify}
            onResend={handleOTPResend}
            onBack={handleBackToForm}
            loading={loading}
          />
        );

      case 'confirmation':
        return (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-6">
              Your consultation has been scheduled successfully. You'll receive a confirmation email shortly.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Booking Details</h3>
              <p className="text-sm text-gray-600">
                <strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Time:</strong> {selectedTime}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Name:</strong> {bookingData?.firstName} {bookingData?.lastName}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Email:</strong> {bookingData?.email}
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {renderStep()}
        </motion.div>
      </div>
    </div>
  );
};

export default BookingPage;
